/**
 * Indexable /players paths (Wave 1 hubs + Wave 2 position children).
 *
 * Cap: only these URLs. Query filters, other years, defense, and unknown
 * position segments stay noindex / 404. Shared by indexabilityPolicy, sitemap,
 * and the app.
 *
 * Keep CURRENT_YEAR aligned with src/constants/app.ts.
 *
 * Wave 2 parse rule: after /players/{stat}/, a numeric segment is a year;
 * wr|te|rb|qb is a position filter when allowlisted with that stat; else 404.
 */

/** @type {number} */
const CURRENT_YEAR = 2026;

/** Yearless player-stat hubs that are indexable (not defense / specialists). */
const PLAYERS_WAVE1_STATS = ['receiving', 'rushing', 'passing'];

/**
 * Yearless Wave 2 player position pages. Path shape: /players/{stat}/{pos}.
 * @type {Array<{ statCategory: string, position: string }>}
 */
const PLAYERS_WAVE2_POSITION_PAGES = [
  { statCategory: 'receiving', position: 'wr' },
  { statCategory: 'receiving', position: 'te' },
  { statCategory: 'rushing', position: 'rb' },
  { statCategory: 'passing', position: 'qb' },
];

/** Tokens accepted as Wave 2 player position path segments. */
const PLAYERS_WAVE2_POSITION_TOKENS = new Set(
  PLAYERS_WAVE2_POSITION_PAGES.map((entry) => entry.position)
);

/**
 * @param {number} [currentYear]
 * @returns {string[]}
 */
function getPlayersWave1Paths(currentYear) {
  const year = currentYear != null ? currentYear : CURRENT_YEAR;
  return [
    '/players',
    ...PLAYERS_WAVE1_STATS.map((stat) => `/players/${stat}`),
    `/players/rookies/${year}`,
    `/players/rookies/${year}/by-college`,
  ];
}

/**
 * @returns {string[]}
 */
function getPlayersWave2Paths() {
  return PLAYERS_WAVE2_POSITION_PAGES.map(
    (entry) => `/players/${entry.statCategory}/${entry.position}`
  );
}

/**
 * All indexable /players paths (Wave 1 + Wave 2).
 * @param {number} [currentYear]
 * @returns {string[]}
 */
function getPlayersIndexablePaths(currentYear) {
  return [...getPlayersWave1Paths(currentYear), ...getPlayersWave2Paths()];
}

/**
 * @param {string} pathname
 * @param {number} [currentYear]
 * @returns {boolean}
 */
function isPlayersWave1Path(pathname, currentYear) {
  const path = String(pathname || '').split('?')[0].replace(/\/$/, '') || '/players';
  return getPlayersWave1Paths(currentYear).includes(path);
}

/**
 * @param {string} pathname
 * @param {number} [currentYear]
 * @returns {boolean}
 */
function isPlayersIndexablePath(pathname, currentYear) {
  const path = String(pathname || '').split('?')[0].replace(/\/$/, '') || '/players';
  return getPlayersIndexablePaths(currentYear).includes(path);
}

/**
 * @param {string|null|undefined} token
 * @returns {boolean}
 */
function isPlayersWave2PositionToken(token) {
  return Boolean(token) && PLAYERS_WAVE2_POSITION_TOKENS.has(String(token).toLowerCase());
}

/**
 * @param {{ statCategory?: string, position?: string|null }} ctx
 * @returns {boolean}
 */
function matchesPlayersWave2Position(ctx) {
  const stat = ctx.statCategory || '';
  const pos = ctx.position ? String(ctx.position).toLowerCase() : '';
  if (!pos) return false;
  return PLAYERS_WAVE2_POSITION_PAGES.some(
    (entry) => entry.statCategory === stat && entry.position === pos
  );
}

/**
 * @param {string} statCategory
 * @param {string} position
 * @returns {string}
 */
function getPlayersPositionPath(statCategory, position) {
  return `/players/${statCategory}/${String(position).toLowerCase()}`;
}

/**
 * Position chips for a Wave 1 player stat hub (clean paths only).
 * @param {string} statCategory
 * @returns {Array<{ position: string, to: string, label: string }>}
 */
function getPlayersWave2ChipsForStat(statCategory) {
  const labels = { wr: 'WR', te: 'TE', rb: 'RB', qb: 'QB' };
  return PLAYERS_WAVE2_POSITION_PAGES.filter((entry) => entry.statCategory === statCategory).map(
    (entry) => ({
      position: entry.position,
      to: getPlayersPositionPath(entry.statCategory, entry.position),
      label: labels[entry.position] || entry.position.toUpperCase(),
    })
  );
}

/**
 * @param {number} year
 * @param {boolean} [byCollege]
 * @returns {string}
 */
function getRookiesPath(year, byCollege) {
  return byCollege ? `/players/rookies/${year}/by-college` : `/players/rookies/${year}`;
}

module.exports = {
  CURRENT_YEAR,
  PLAYERS_WAVE1_STATS,
  PLAYERS_WAVE2_POSITION_PAGES,
  PLAYERS_WAVE2_POSITION_TOKENS,
  getPlayersWave1Paths,
  getPlayersWave2Paths,
  getPlayersIndexablePaths,
  isPlayersWave1Path,
  isPlayersIndexablePath,
  isPlayersWave2PositionToken,
  matchesPlayersWave2Position,
  getPlayersPositionPath,
  getPlayersWave2ChipsForStat,
  getRookiesPath,
};
